import { api } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import { ServiceProviderDomain } from "@/models";
import {
  getAllServiceProvidersQueryOptions,
  getServiceProviderQueryOptions,
  getServiceProvidersQueryOptions,
} from "./get.hooks";

const toastDomainMessageMap: Record<ServiceProviderDomain, string> = {
  service: "toast.serviceProviderProviderDeleted",
  provider: "toast.serviceProviderServiceDeleted",
};

const toastDomainErrorMessageMap: Record<ServiceProviderDomain, string> = {
  service: "toast.deleteServiceProviderProviderError",
  provider: "toast.deleteServiceProviderServiceError",
};

export function useDeleteServiceProvider(
  domain: ServiceProviderDomain,
  domainId: string,
  opts?: {
    onSuccess?: () => void;
    onError?: () => void;
  },
) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/service-providers/$id`, undefined, {
        pathReplace: { $id: id },
      }),
    onSuccess: (_, id) => {
      queryClient.setQueryData(
        getServiceProvidersQueryOptions(domain, domainId).queryKey,
        (old) => old?.filter((sp) => sp.id !== id),
      );
      queryClient.setQueryData(
        getAllServiceProvidersQueryOptions.queryKey,
        (old) => old?.filter((sp) => sp.id !== id),
      );
      queryClient.resetQueries({
        queryKey: getServiceProviderQueryOptions(id).queryKey,
      });
      toast({
        title: toastDomainMessageMap[domain],
        variant: "success",
      });
      opts?.onSuccess?.();
    },
    onError: () => {
      toast({
        title: toastDomainErrorMessageMap[domain],
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
